import { zodResolver } from '@hookform/resolvers/zod';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import type { SubmitHandler } from 'react-hook-form';
import { useNotifications } from 'utils/hooks/useNotifications';
import { inheritForTargetType } from '../ruleTypes';
import type { AutoAssignmentRule } from '../rulesApi';
import { useRulesApi } from '../rulesApi';
import { RuleFormBody } from './RuleFormBody';
import { RuleFormFooter } from './RuleFormFooter';
import { conditionsToFilter, filterToConditions } from './ruleFilterMapping';
import {
  type RuleFormSubmitValues,
  type RuleFormValues,
  ruleFormSchema,
} from './ruleFormSchema';
import { useRuleFormViews } from './useRuleFormViews';

type RuleFormAddProps = {
  formType: 'add';
  onClose: () => void;
  onSave: () => void;
};

type RuleFormEditProps = {
  formType: 'edit';
  rule: AutoAssignmentRule;
  onClose: () => void;
  onSave: () => void;
};

type RuleFormProps = RuleFormAddProps | RuleFormEditProps;

export const RuleForm = (props: RuleFormProps) => {
  const { formType, onClose, onSave } = props;
  const rule = formType === 'edit' ? props.rule : undefined;
  const { showNotification } = useNotifications();
  const { createRule, updateRule } = useRulesApi();
  const type = rule?.type ?? 'device';
  const initialConditions = rule ? filterToConditions(rule.filter) : [];
  const initialInherit = inheritForTargetType(type, rule?.inherit ?? false);

  const form = useCustomForm<RuleFormValues, unknown, RuleFormSubmitValues>({
    defaultValues: {
      type,
      description: rule?.description ?? '',
      conditions: initialConditions,
      labels: rule?.labels ?? [],
      inherit: initialInherit,
    },
    resolver: zodResolver(ruleFormSchema),
  });

  const { control, formState, handleSubmit, setValue, getValues, reset } = form;
  const { errors, isSubmitting, isDirty } = formState;

  const views = useRuleFormViews({ control, setValue, formType, rule });

  const handleSaveClick = () => {
    if (formType === 'edit' && !isDirty) {
      onClose();
      return;
    }
    void handleSubmit(onSubmit)();
  };

  const onSubmit: SubmitHandler<RuleFormSubmitValues> = async (data) => {
    const { conditions, type, ...rest } = data;
    const filter = conditionsToFilter(conditions);
    if (formType === 'add') {
      await createRule({ ...rest, type, filter });
    } else {
      await updateRule(rule!.id, { ...rest, filter });
    }

    showNotification({
      type: 'success',
      message: formType === 'add' ? 'Rule created' : 'Rule updated',
    });
    onSave();
  };

  return (
    <SidePanel
      title={views.panelTitle}
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={({ closeModal }) => (
        <RuleFormFooter
          view={views.view}
          closeModal={closeModal}
          formType={formType}
          isSubmitting={isSubmitting}
          canTest={views.canTest}
          hasTestModifications={views.hasTestModifications}
          pickerDraftLabelCount={views.pickerDraftLabels.length}
          onApplyPickerDraft={views.applyPickerDraft}
          onGoToMainView={views.goToMainView}
          onSave={handleSaveClick}
          onOpenTestView={views.openTestView}
          onApplyTestChanges={views.applyTestChanges}
          onRevertTestChanges={views.revertTestChanges}
        />
      )}
    >
      <RuleFormBody
        view={views.view}
        formType={formType}
        control={control}
        errors={errors}
        setValue={setValue}
        getValues={getValues}
        reset={reset}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        onOpenLabelPicker={views.openLabelPicker}
        pickerDraftLabels={views.pickerDraftLabels}
        onPickerDraftLabelsChange={views.setPickerDraftLabels}
        canTest={views.canTest}
        testSnapshot={views.testSnapshot}
        onRunTest={views.runTest}
        onClearTestSnapshot={views.clearTestSnapshot}
      />
    </SidePanel>
  );
};
